import {
  clearStoredAdminSession,
  isImpersonatingToken,
  setImpersonationToken,
} from '@/features/impersonation-session';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';

// Public hand-off route used by the Account 360 Console (web-allo-console)
// to drop an impersonation token into this tab and bounce into the app.
//
// Console flow:
//   1. POST /v1/admin/user/{pid}/impersonate → mints a 15-min JWT carrying
//      the `impersonated_by` claim
//   2. window.open(`${VITE_WEBAPP_URL}/impersonate?token=…`, '_blank',
//      'noopener')
//
// We intentionally do NOT preserve an admin session here (unlike the
// in-app `/internal/users` flow): the staff member's real session lives
// on the console tab. If this tab has no Supabase session, the `_app`
// loader bounces to /login; the staff logs in as themselves, and on the
// next loader pass the impersonation token in sessionStorage takes effect
// (getActiveAccessToken prefers the impersonation token).
//
// Token in sessionStorage means it lives only for this tab — closing the
// tab is the natural "stop impersonating" exit. Inside the app, the
// existing ImpersonationBanner ("Stop impersonating") handles the in-app
// revert path.
export const Route = createFileRoute('/impersonate')({
  validateSearch: z.object({
    token: z.string().min(1).optional(),
  }),
  beforeLoad: ({ search }) => {
    // Reject anything that isn't a JWT carrying `impersonated_by`.
    // Without this check a bad token would be written to sessionStorage,
    // 401 on /v1/user, and ImpersonationBanner wouldn't render (it gates
    // on the decoded claim) — leaving no in-app recovery path.
    if (!search.token || !isImpersonatingToken(search.token)) {
      throw redirect({ to: '/' });
    }

    // The console hand-off has no admin session of its own to preserve.
    // If this tab previously impersonated via /internal/users, the stored
    // admin session belongs to a different staff member — clear it so the
    // banner reads the correct owner and "Stop impersonating" doesn't
    // restore the wrong session.
    clearStoredAdminSession();
    setImpersonationToken(search.token);

    // Strip the token from the URL so it doesn't leak into browser
    // history, Sentry/NewRelic breadcrumbs, server access logs, or
    // URL-reading extensions. The token is single-use-for-this-tab.
    window.history.replaceState({}, '', '/');
    throw redirect({ to: '/' });
  },
});
